import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CategoryModal from './CategoryModal';
import SalesExecutiveCommissionSlabModal from './SalesExecutiveCommissionSlabModal';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';

function CategoryIndex() {
  // Session validation
  useSessionValidation();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, logout, token } = useAuth();
  const { logAudit } = useAuditLog();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Commission slab modal states
  const [commissionSlabOpen, setCommissionSlabOpen] = useState(false);
  const [commissionSlabCategory, setCommissionSlabCategory] = useState(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return; // Prevent calls without required data

    setLoading(true);
    fetchtableData().finally(() => {
      setLoading(false);
    });
  }, [companyId, token]);

  const fetchtableData = async () => {
    try {
      const response = await fetchData('CategoryApi', `/${companyId}`, token);
      console.log('Category data response:', response);

      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
        console.log('Category data loaded successfully');
      } else {
        console.log('Category API failed or returned no data');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Category Details:', error);
      setData([]);
    }
  };

  const handleDelete = async () => {
    deleteData('CategoryApi', `${deleteItemId}`, token)
      .then(() => {
        fetchtableData(); // Refresh data after deletion
        setDeleteItemId(null); // Reset the item ID
        setConfirmDialogOpen(false); // Close the dialog
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });
  };

  const handleActivate = (itemId) => {
    activateData('CategoryApi', `${itemId}`, token)
      .then(() => {
        fetchtableData(); // Refresh data after activation
      })
      .catch((error) => {
        console.error('Error activating record:', error);
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  // Modal handlers
  const handleOpenModal = (mode, category = null) => {
    setModalMode(mode);
    setSelectedCategory(category);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCategory(null);
  };

  const handleSaveCategory = () => {
    fetchtableData(); // Refresh data
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter data based on search term
  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.Name?.toLowerCase().includes(searchLower) ||
      item.HSNCode?.toLowerCase().includes(searchLower) ||
      item.GSTRate?.toString().includes(searchLower)
    );
  });

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Calculate pagination values
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Define table columns
  const columns = [
    { field: 'Name', header: 'Category Name', align: 'left' },
    { field: 'GSTRate', header: 'GST Rate (%)', align: 'right' },
    { field: 'HSNCode', header: 'HSN Code', align: 'left' },
  ];

  // Define action buttons for each row
  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      

      <Tooltip title="View Details" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('view', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          👁️
        </Button>
      </Tooltip>

      <Tooltip title="Edit Category" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('edit', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          ✏️
        </Button>
      </Tooltip>

      <Tooltip title={item.IsActive ? "Delete Category" : "Activate Category"} arrow>
        <Button
          size="small"
          onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: item.IsActive
              ? '0 1px 4px rgba(255, 107, 107, 0.3)'
              : '0 1px 4px rgba(78, 205, 196, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          {item.IsActive ? '🗑️' : '✅'}
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        {loading && <LogoLoader open={loading} message="Loading categories..." />}

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          {/* Header Section with Title */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Romans',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Category</span>
                </h4>

              </div>
            </div>
          </div>

          {/* Search Bar and Create Button in Same Row */}
          <div style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, HSN code, or GST rate..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#667eea', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '13px',
                  height: '29px',
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            />
            <Tooltip title="Create a new category" arrow>
              <Button
                variant="contained"
                onClick={() => handleOpenModal('create')}
                className="action-button"
                style={{
                  background: 'linear-gradient(135deg,  #2C3E6E 0%, #2C3E6E 100%)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                  border: 'none',
                  minHeight: '29px',
                  textTransform: 'none',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ fontSize: '12px' }}>+</span>
                Create Category
              </Button>
            </Tooltip>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="category table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg,  #2C3E6E 0%, #2C3E6E 100%)',
                    height: '27px'
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '4px 12px',
                        textAlign: column.align || 'left',
                        height: '27px'
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '4px 12px',
                      textAlign: 'right',
                      height: '27px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => (
                    <TableRow key={item.Id} className="table-row" style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.Name}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right' }}>
                        {item.GSTRate}%
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.HSNCode}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {renderActions(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this category record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span>
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Category Modal */}
        <CategoryModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveCategory}
          categoryData={selectedCategory}
          mode={modalMode}
          existingCategories={data}
        />

        {/* Sales Executive Commission Slab Modal */}
        <SalesExecutiveCommissionSlabModal
          open={commissionSlabOpen}
          onClose={() => { setCommissionSlabOpen(false); setCommissionSlabCategory(null); }}
          onSave={() => {}}
          item={commissionSlabCategory}
        />

      </div>
    </Fade >
  );
}

export default CategoryIndex;
